import {
  Box,
  Button,
  Container,
  Divider,
  Heading,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { NestIcon } from "./NestIcon";
import { BirdNestIcon } from "./BirdNestIcon";
import { FilesIcon } from "./FilesIcon";
import { TargetIcon } from "./TargetIcon";
import { HandshakeIcon } from "./HandshakeIcon";
import { CalendarIcon } from "./CalendarIcon";
import { Link } from "@chakra-ui/next-js";
import { InvoiceIcon } from "./InvoiceIcon";
import { EmailIcon } from "./EmailIcon";
import { CheckIcon } from "./CheckIcon";
import { motion, useInView } from "framer-motion"; // Import useInView
import { useRef } from "react"; // Import useRef
import { LetsClarifayoLink } from "./LetsClarifayoLink";

const MakeDifferenceItem = ({
  icon,
  description,
  flexDirection = "row",
}: {
  icon: React.ReactNode;
  description: string;
  flexDirection?: "row" | "column" | "row-reverse" | "column-reverse";
}) => {
  const itemRef = useRef(null);
  const itemInView = useInView(itemRef, { once: true, margin: "-200px" }); // Adjust margin

  const revealVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <motion.div
      ref={itemRef}
      initial="hidden"
      animate={itemInView ? "visible" : "hidden"}
      variants={revealVariants}
    >
      <VStack>
        <Divider borderColor="black.900" orientation="vertical" h={24} />
        <Stack
          direction={["column", flexDirection]}
          gap={8}
          w="full"
          alignItems={"center"}
        >
          <Box>{icon}</Box>
          <Heading
            flex={1}
            fontFamily={"Gotham"}
            fontSize={24}
            maxWidth={600}
            fontWeight={600}
          >
            {description}
          </Heading>
        </Stack>
      </VStack>
    </motion.div>
  );
};

export const MakeDifference = () => {
  const t = useTranslations();
  const containerRef = useRef(null);
  const containerInView = useInView(containerRef, {
    once: true,
    margin: "-100px",
  }); // Adjust margin

  const revealVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <motion.div
      ref={containerRef}
      initial="hidden"
      animate={containerInView ? "visible" : "hidden"}
      variants={revealVariants}
    >
      <Box
        bgColor="brand.100"
        w="full"
        h="full"
        pb={{
          base: 12,
          md: 20,
        }}
      >
        <Container maxW="container.xl">
          <Stack
            direction={["column", "row"]}
            w="full"
            h="full"
            py={{ base: 16, md: 20 }}
            px={{
              base: 4,
              md: 20,
            }}
            bgColor="brand.100"
            gap={{
              base: 8,
              md: 16,
            }}
            mb={8}
          >
            <Box w="full" h="full">
              <Heading fontFamily={"Gotham"}>
                {t("MakeDifference.title")}
              </Heading>
            </Box>
            <Box w="full" h="full">
              <Text
                fontWeight={400}
                fontSize={"xl"}
                textAlign={{
                  base: "center",
                  md: "left",
                }}
                dangerouslySetInnerHTML={{
                  __html: t.raw("MakeDifference.description"),
                }}
              ></Text>
            </Box>
          </Stack>
          <VStack
            w="full"
            gap={8}
            px={{
              base: 4,
              md: 20,
            }}
          >
            <NestIcon width={80} height={40} />
            <MakeDifferenceItem
              icon={<FilesIcon width={140} height={140} />}
              description={t("MakeDifference.makeDifference1")}
            />
            <MakeDifferenceItem
              icon={<TargetIcon width={140} height={140} />}
              description={t("MakeDifference.makeDifference2")}
              flexDirection="row-reverse"
            />
            <MakeDifferenceItem
              icon={<HandshakeIcon width={140} height={140} />}
              description={t("MakeDifference.makeDifference3")}
            />
            <MakeDifferenceItem
              icon={<CalendarIcon width={140} height={140} />}
              description={t("MakeDifference.makeDifference4")}
              flexDirection="row-reverse"
            />
            <MakeDifferenceItem
              icon={<EmailIcon width={140} height={140} />}
              description={t("MakeDifference.makeDifference5")}
            />
            <MakeDifferenceItem
              icon={<CheckIcon width={140} height={140} />}
              description={t("MakeDifference.makeDifference6")}
              flexDirection="row-reverse"
            />
            <MakeDifferenceItem
              icon={<InvoiceIcon width={140} height={140} />}
              description={t("MakeDifference.makeDifference7")}
            />
            <Divider borderColor="black.900" orientation="vertical" h={24} />
            <BirdNestIcon width={80} height={80} />
            <LetsClarifayoLink />
          </VStack>
        </Container>
      </Box>
    </motion.div>
  );
};
